import React, { useState } from 'react';
import { X, User, Briefcase, Building, Calendar, TrendingUp, FileText, Activity, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Button } from './ui/button';
import NotesTab from './shared/NotesTab';
import ActivityTab from './shared/ActivityTab';

const SubmissionDetailModal = ({ submission, isOpen, onClose, onEdit }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [currentUser, setCurrentUser] = useState(null);

  React.useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, []);

  if (!submission) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Submission': 'bg-blue-100 text-blue-800',
      'Client Submission': 'bg-indigo-100 text-indigo-800',
      'Interview': 'bg-yellow-100 text-yellow-800',
      'Offer Extended': 'bg-purple-100 text-purple-800',
      'Offer Accepted': 'bg-green-100 text-green-800',
      'Onboarding Initiated': 'bg-teal-100 text-teal-800',
      'Onboarded': 'bg-emerald-100 text-emerald-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="submission-detail-modal">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="h-6 w-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-foreground" data-testid="submission-header">
                Submission Details
              </h2>
            </div>
            <div className="space-y-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{submission.candidate_name || 'Unknown Candidate'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                <span>{submission.job_title || 'Unknown Job'}</span>
              </div>
              {submission.client_name && (
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  <span>{submission.client_name}</span>
                </div>
              )}
            </div>
          </div>
          {submission.current_status && (
            <Badge className={getStatusColor(submission.current_status)} data-testid="submission-status-badge">
              {submission.current_status}
            </Badge>
          )}
        </div>

        <Separator className="mb-4" />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mb-4" data-testid="submission-detail-tabs">
            <TabsTrigger value="details" className="gap-2" data-testid="tab-details">
              <FileText className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2" data-testid="tab-activity">
              <Activity className="h-4 w-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="notes" className="gap-2" data-testid="tab-notes">
              <MessageSquare className="h-4 w-4" />
              Notes
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            {/* Details Tab */}
            <TabsContent value="details" className="space-y-6 mt-0" data-testid="details-content">
              {/* Submission Information */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90">Submission Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-primary mt-0.5" />
                    <div>
                      <span className="text-muted-foreground">Submitted:</span>
                      <span className="ml-1 font-medium">{formatDate(submission.submission_date)}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <User className="h-4 w-4 text-primary mt-0.5" />
                    <div>
                      <span className="text-muted-foreground">Submitted By:</span>
                      <span className="ml-1 font-medium">{submission.submitted_by_name || submission.recruiter_name}</span>
                    </div>
                  </div>
                  {submission.match_score !== undefined && (
                    <div className="flex items-start gap-2 text-sm">
                      <TrendingUp className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Match Score:</span>
                        <span className="ml-1 font-medium">{Math.round(submission.match_score)}%</span>
                      </div>
                    </div>
                  )}
                  {submission.priority_level && (
                    <div className="flex items-start gap-2 text-sm">
                      <FileText className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Priority:</span>
                        <span className="ml-1 font-medium capitalize">{submission.priority_level}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Status History */}
              {submission.status_history && submission.status_history.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground/90">Status History</h3>
                  <div className="space-y-2">
                    {submission.status_history.slice(0, 5).map((history, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <Badge className={getStatusColor(history.status)}>
                            {history.status}
                          </Badge>
                          {history.changed_by && (
                            <p className="text-xs text-muted-foreground mt-1">by {history.changed_by}</p>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">{formatDate(history.changed_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Interview Count */}
              {submission.interview_count > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90">Interviews</h3>
                    <p className="text-sm text-foreground/80">
                      {submission.interview_count} interview{submission.interview_count !== 1 ? 's' : ''} scheduled
                    </p>
                  </div>
                </>
              )}

              {/* Internal Notes from submission */}
              {submission.internal_notes && submission.internal_notes.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90">Submission Notes</h3>
                    {submission.internal_notes.map((note, index) => (
                      <div key={index} className="p-3 bg-muted/20 rounded-lg">
                        <p className="text-sm text-foreground/80 whitespace-pre-wrap">{note.note || note}</p>
                        {note.created_at && (
                          <p className="text-xs text-muted-foreground mt-2">{formatDate(note.created_at)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-4 mt-0" data-testid="activity-content">
              <ActivityTab entityType="SUBMISSION" entityId={submission?.id} />
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="space-y-4 mt-0" data-testid="notes-content">
              <NotesTab entityType="SUBMISSION" entityId={submission?.id} currentUser={currentUser} />
            </TabsContent>
          </div>
        </Tabs>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose} data-testid="close-modal-btn">
            Close
          </Button>
          {onEdit && (
            <Button variant="default" onClick={() => onEdit(submission)} data-testid="edit-submission-btn">
              Edit Submission
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubmissionDetailModal;
